import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { themeColors } from '../styles/theme';
import Fonts from './Fonts';
import { PubObject } from './FindPubs';
import { useNavigate } from 'react-router-dom';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import colormap from 'colormap';

// Import leaflet and CSS
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Set up Leaflet icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface MapViewProps {
  latitude: number;
  longitude: number;
  pubsToView: PubObject[];
}

// Configuration for trend calculation
const TREND_CONFIG = {
  // Number of future timestamps to average for trend calculation
  futureTimestampsToAverage: 2,
  // Threshold difference required to determine a trend (up or down)
  // i.e., if the average future value is greater than current + threshold, it's 'up' (et. v.v.)
  trendThreshold: 0.02 // 2% difference
};

// Create a continuous colour scale using the 'hot' colormap theme
const sunExposureColors = colormap({
  colormap: 'hot',
  nshades: 100,
  format: 'rgbaString',
  alpha: 1
});

// Function to get colour from the hot colormap based on sun exposure percentage
const getSunExposureColor = (percentage: number): string => {
  // Map percentage (0-1) to colormap index (0-99)
  const colorIndex = Math.min(Math.floor(percentage * 99), 99);
  return sunExposureColors[colorIndex];
};

const MapView: React.FC<MapViewProps> = ({ latitude, longitude, pubsToView }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [renderKey, setRenderKey] = useState(Date.now()); // Force re-render when needed
  const navigate = useNavigate();
  
  // New state for custom location
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  
  // Check if using custom location on mount
  useEffect(() => {
    const searchMode = localStorage.getItem('searchMode');
    setIsCustomLocation(searchMode === 'custom');
  }, []);

  // Handle edit search area
  const handleEditSearchArea = () => {
    navigate('/search-area');
  };

  // Helper to get the nearest timestamp's sun value with sun prediction
  const getCurrentSunExposure = (pub: PubObject): { value: number, trend: 'up' | 'down' | 'flat', nextSunTime?: number, sunDuration?: number } => {
    // Enhanced debug logging to diagnose issues
    
    if (!pub.heatmap) {
      return { value: 0, trend: 'flat' };
    }
    
    if (!pub.heatmap.timestamps || !Array.isArray(pub.heatmap.timestamps)) {
      return { value: 0, trend: 'flat' };
    }
    
    if (!pub.heatmap.values || !Array.isArray(pub.heatmap.values)) {
      return { value: 0, trend: 'flat' };
    }
    
    if (pub.heatmap.timestamps.length === 0) {
      return { value: 0, trend: 'flat' };
    }

    const now = new Date();
    const timestamps = pub.heatmap.timestamps;
    const values = pub.heatmap.values;
    
    // Get current time in minutes from midnight (ignoring date)
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Find the nearest timestamp by comparing minutes from midnight only (ignoring date)
    let nearestIndex = 0;
    let minMinutesDiff = Infinity;
    
    timestamps.forEach((timestamp, index) => {
      try {
        const timestampDate = new Date(timestamp);
        
        // Get this time in minutes from midnight
        const timeMinutes = timestampDate.getHours() * 60 + timestampDate.getMinutes();
        
        // Calculate difference, handling the wrap around midnight case
        let minutesDiff = Math.abs(timeMinutes - currentMinutes);
        // Consider times that wrap around midnight (e.g. 23:50 vs 00:10)
        minutesDiff = Math.min(minutesDiff, 1440 - minutesDiff);
        
        if (minutesDiff < minMinutesDiff) {
          minMinutesDiff = minutesDiff;
          nearestIndex = index;
        }
      } catch (e) {
        // Just skip problematic timestamps
      }
    });
    
    // Make sure the indices are in bounds
    if (nearestIndex >= values.length) {
      console.error(`[MapView] Index out of bounds: ${nearestIndex} >= ${values.length}`);
      return { value: 0, trend: 'flat' };
    }
    
    // Get the current value (0-1 percentage)
    const currentValue = typeof values[nearestIndex] === 'number' ? values[nearestIndex] : 0;
    
    // Determine the trend by comparing with the average of next N values
    let trend: 'up' | 'down' | 'flat' = 'flat';
    const { futureTimestampsToAverage, trendThreshold } = TREND_CONFIG;
    
    if (nearestIndex < values.length - 1) {
      // Calculate average of next N timestamps
      let sumFutureValues = 0;
      let countFutureValues = 0;
      
      for (let i = 1; i <= futureTimestampsToAverage; i++) {
        const futureIndex = nearestIndex + i;
        if (futureIndex < values.length) {
          const futureValue = typeof values[futureIndex] === 'number' ? values[futureIndex] : 0;
          sumFutureValues += futureValue;
          countFutureValues++;
        }
      }
      
      if (countFutureValues > 0) {
        const averageFutureValue = sumFutureValues / countFutureValues;
        
        // Determine trend based on threshold
        if (averageFutureValue > currentValue + trendThreshold) {
          trend = 'up';
        } else if (averageFutureValue < currentValue - trendThreshold) {
          trend = 'down';
        }
      }
    }
    
    // Calculate sun predictions
    let nextSunTime: number | undefined = undefined;
    let sunDuration: number | undefined = undefined;
    
    // If currently no sun (0%), calculate when it will next be in sun (>5%)
    if (currentValue === 0 && nearestIndex < values.length - 1) {
      for (let i = nearestIndex + 1; i < values.length; i++) {
        if (typeof values[i] === 'number' && values[i] > 0.05) {
          // Calculate hours until sun (each timestep is 15 minutes)
          nextSunTime = (i - nearestIndex) * 0.25;
          break;
        }
      }
    }
    // If currently in sun (>0%), calculate how long until it's at 0%
    else if (currentValue > 0 && nearestIndex < values.length - 1) {
      for (let i = nearestIndex + 1; i < values.length; i++) {
        if (typeof values[i] === 'number' && values[i] === 0) {
          // Calculate hours of sun remaining (each timestep is 15 minutes)
          sunDuration = (i - nearestIndex) * 0.25;
          break;
        }
      }
      
      // If we reach the end of the data and still have sun, set to remaining timesteps
      if (sunDuration === undefined) {
        sunDuration = (values.length - nearestIndex - 1) * 0.25;
      }
    }
    
    return { 
      value: currentValue, 
      trend,
      nextSunTime,
      sunDuration
    };
  };

  // Create a continuous coloured marker icon based on sun exposure using the hot colormap
  const createSunExposureIcon = (sunExposure: number, trend: 'up' | 'down' | 'flat', needsGardenSubmission?: boolean, pubName?: string) => {
    // Log what we're creating for debugging with pub name for context    
    // Create a temporary svg element
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svg.setAttribute("width", "24");
    svg.setAttribute("height", "24");
    svg.setAttribute("viewBox", "0 0 24 24");

    // Only create the colored circle for pubs with gardens defined
    if (needsGardenSubmission !== true) {
      // Choose the colour based on sun exposure using the continuous colormap
      const exposureColor = getSunExposureColor(sunExposure);

      // Create the marker shape (just a circle)
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", "12");
      circle.setAttribute("cy", "12");
      circle.setAttribute("r", "10");
      circle.setAttribute("fill", exposureColor);
      circle.setAttribute("stroke", "white");
      circle.setAttribute("stroke-width", "2");
      svg.appendChild(circle);
    }

    // Super simple trend indicator - just a single line arrow
    if (trend === 'up' || trend === 'down') {
      
      // Add a simple arrow
      const arrow = document.createElementNS("http://www.w3.org/2000/svg", "path");
      if (trend === 'up') {
        // Simple up arrow - straight line with arrowhead
        arrow.setAttribute("d", "M18,14 L18,6 M16,8 L18,6 L20,8");
      } else {
        // Simple down arrow - straight line with arrowhead
        arrow.setAttribute("d", "M18,6 L18,14 M16,12 L18,14 L20,12");
      }
      arrow.setAttribute("stroke", trend === 'up' ? "#4CAF50" : "#F44336");
      arrow.setAttribute("stroke-width", "2");
      arrow.setAttribute("fill", "none");
      arrow.setAttribute("stroke-linecap", "round");
      arrow.setAttribute("stroke-linejoin", "round");
      svg.appendChild(arrow);
    }

    // Add exclamation mark for pubs with missing gardens
    if (needsGardenSubmission === true) {
      // Simple exclamation mark
      const exclamation = document.createElementNS("http://www.w3.org/2000/svg", "path");
      exclamation.setAttribute("d", "M12,5 L12,12 M12,14 L12,16");
      exclamation.setAttribute("stroke", "#FF6347");
      exclamation.setAttribute("stroke-width", "2");
      exclamation.setAttribute("stroke-linecap", "round");
      svg.appendChild(exclamation);
    }

    // Convert the SVG to a data URL
    const svgString = new XMLSerializer().serializeToString(svg);
    
    // UTF-8 safe base64 encoding
    const base64 = window.btoa(unescape(encodeURIComponent(svgString)));
    const dataUrl = 'data:image/svg+xml;base64,' + base64;

    // Create and return the icon
    return L.icon({
      iconUrl: dataUrl,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  };

  // Effect to ensure container has dimensions before initialising
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    const checkContainerSize = () => {
      const rect = mapContainerRef.current?.getBoundingClientRect();
      if (rect && (rect.width < 200 || rect.height < 100)) {
        // Force a re-render after a delay
        setTimeout(() => setRenderKey(Date.now()), 100);
      }
    };
    
    checkContainerSize();
    
    // Also add resize handler
    window.addEventListener('resize', checkContainerSize);
    return () => window.removeEventListener('resize', checkContainerSize);
  }, [renderKey]);

  // Initialise map
  useEffect(() => {
    // Small delay before initialising map to ensure DOM is ready
    const initTimeout = setTimeout(() => {
      
      // Clean up any previous map instance
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersLayerRef.current = null;
      }
      
      // Verify container exists and has dimensions
      if (!mapContainerRef.current) {
        return;
      }
      
      // Log container dimensions
      const rect = mapContainerRef.current.getBoundingClientRect();
      
      // If container has no dimensions, try again later
      if (rect.width < 200 || rect.height < 100) {
        setRenderKey(Date.now()); // Force re-render
        return;
      }
      
      try {
        // Ensure container is visible
        if (mapContainerRef.current) {
          mapContainerRef.current.style.display = 'block';
          mapContainerRef.current.style.height = '100%';
          mapContainerRef.current.style.width = '100%';
          mapContainerRef.current.style.minWidth = '100%';
        }
        
        // Map creation
        const map = L.map(mapContainerRef.current, {
          attributionControl: false, // Disable attribution for cleaner look
          zoomControl: true,         // Enable zoom controls
          scrollWheelZoom: true      // Enable scroll wheel zoom
        });
        
        // Wait for next tick before setting view to ensure container is ready
        setTimeout(() => {
          map.setView([latitude, longitude], 13);
          
          // Store reference to map
          mapRef.current = map;
          
          // Create a layer group for markers
          const markersLayer = L.layerGroup().addTo(map);
          markersLayerRef.current = markersLayer;
          
          // Add tile layer with Google-style maps
          L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(map);
                    
          // Need to invalidate size immediately and after a delay
          map.invalidateSize();
          
          // Add markers with a delay to ensure map is ready
          setTimeout(() => {
            setMapInitialized(true);
            addPubMarkers();
            
            // Invalidate size again after markers are added
            if (mapRef.current) {
              mapRef.current.invalidateSize();
            }
          }, 300);
        }, 50);
        
        // Clean up on unmount
        return () => {          
          if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
            markersLayerRef.current = null;
          }
        };
      } catch (error) {
        console.error(`Error creating map: ${error instanceof Error ? error.message : String(error)}`);
      }
    }, 300); // Increased delay before initialising
    
    return () => clearTimeout(initTimeout);
  }, [latitude, longitude, renderKey]); // Re-init if lat/lng changes or we force a re-render
  
  // Function to add pub markers
  const addPubMarkers = () => {
    if (!mapRef.current || !markersLayerRef.current) {
      return;
    }
    
    try {
      // Clear existing markers
      markersLayerRef.current.clearLayers();
      
      // Create markers array for bounds calculation
      const markers = [L.latLng(latitude, longitude)];
      
      // Add user location marker
      const userMarker = L.circleMarker([latitude, longitude], {
        radius: 8,
        fillColor: themeColors.blue,
        color: '#FFFFFF',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9
      }).addTo(markersLayerRef.current);
      
      userMarker.bindPopup("Your Location");
      
      // Count how many pubs we're actually adding
      let addedPubCount = 0;
      let skippedPubCount = 0;
      
      // Add markers for each pub
      pubsToView.forEach(pub => {
        // Skip pubs without coordinates (should be rare)
        if (!pub.latitude || !pub.longitude) {
          skippedPubCount++;
          return;
        }
        
        addedPubCount++;
        
        // Add to markers for bounds calculation
        markers.push(L.latLng(pub.latitude, pub.longitude));
        
        // Get current sun exposure with predictions
        const { value: sunExposure, trend, nextSunTime, sunDuration } = getCurrentSunExposure(pub);
        
        // Use sun exposure based icon with continuous color
        const icon = createSunExposureIcon(
          sunExposure, 
          trend, 
          pub.garden_drawn === false,
          pub.name // Pass pub name for debugging context
        );
        
        // Create marker
        const marker = L.marker([pub.latitude, pub.longitude], { icon }).addTo(markersLayerRef.current!);
        
        // Create more compact popup content
        const popupContent = document.createElement('div');
        popupContent.style.padding = '8px';
        popupContent.style.maxWidth = '180px';
        popupContent.style.borderRadius = '6px';
        
        // Add pub name - more compact
        const nameElement = document.createElement('h3');
        nameElement.textContent = pub.name;
        nameElement.style.fontWeight = 'bold';
        nameElement.style.fontSize = '0.95rem';
        nameElement.style.marginBottom = '4px';
        nameElement.style.fontFamily = "ApfelGrotezkRegular";
        nameElement.style.color = themeColors.darkBackground;
        popupContent.appendChild(nameElement);
        
        // Compact info section
        const infoContainer = document.createElement('div');
        infoContainer.style.fontSize = '0.8rem';
        infoContainer.style.marginBottom = '6px';
        
        // Add distance in a more compact way
        infoContainer.innerHTML = `<span style="color:${themeColors.blue};font-weight:500;">${Math.round(pub.distance)}m away</span>`;
        
        // Add address only if it's short
        if (pub.address && pub.address.length < 30) {
          infoContainer.innerHTML += `<br>${pub.address}`;
        }
        
        popupContent.appendChild(infoContainer);
        
        // Add sun exposure information in a more compact layout
        if (sunExposure > 0 || pub.rating) {
          const sunContainer = document.createElement('div');
          sunContainer.style.display = 'flex';
          sunContainer.style.flexDirection = 'column';
          sunContainer.style.gap = '2px';
          
          // Current sun status - more compact, single line
          const sunStatusContainer = document.createElement('div');
          sunStatusContainer.style.display = 'flex';
          sunStatusContainer.style.alignItems = 'center';
          sunStatusContainer.style.justifyContent = 'space-between';
          
          const sunStatusLabel = document.createElement('span');
          sunStatusLabel.textContent = 'Current Sun:';
          sunStatusLabel.style.fontWeight = '500';
          sunStatusLabel.style.fontSize = '0.75rem';
          sunStatusContainer.appendChild(sunStatusLabel);
          
          const sunPercentage = document.createElement('span');
          // Format as percentage with proper decimal places
          sunPercentage.textContent = `${(sunExposure * 100).toFixed(0)}%`;
          sunPercentage.style.fontWeight = 'bold';
          sunPercentage.style.fontSize = '0.75rem';
          sunPercentage.style.color = 'black';
          sunStatusContainer.appendChild(sunPercentage);
          
          sunContainer.appendChild(sunStatusContainer);
          
          // Always add trend, even if it's flat
          const trendContainer = document.createElement('div');
          trendContainer.style.display = 'flex';
          trendContainer.style.alignItems = 'center';
          trendContainer.style.justifyContent = 'space-between';
          
          const trendLabel = document.createElement('span');
          trendLabel.textContent = 'Trend:';
          trendLabel.style.fontWeight = '500';
          trendLabel.style.fontSize = '0.75rem';
          trendContainer.appendChild(trendLabel);
          
          const trendValue = document.createElement('span');
          if (trend === 'up') {
            trendValue.textContent = 'Getting sunnier';
            trendValue.style.color = '#4CAF50';
          } else if (trend === 'down') {
            trendValue.textContent = 'Getting shadier';
            trendValue.style.color = '#F44336';
          } else {
            trendValue.textContent = 'Stable';
            trendValue.style.color = '#666';
          }
          trendValue.style.fontWeight = 'bold';
          trendValue.style.fontSize = '0.75rem';
          trendContainer.appendChild(trendValue);
          
          sunContainer.appendChild(trendContainer);
          
          // Add sun prediction information based on current state
          if (sunExposure === 0 && nextSunTime !== undefined) {
            const predictionContainer = document.createElement('div');
            predictionContainer.style.display = 'flex';
            predictionContainer.style.alignItems = 'center';
            predictionContainer.style.justifyContent = 'space-between';
            
            const predictionLabel = document.createElement('span');
            predictionLabel.textContent = 'Next in sun:';
            predictionLabel.style.fontWeight = '500';
            predictionLabel.style.fontSize = '0.75rem';
            predictionContainer.appendChild(predictionLabel);
            
            const predictionValue = document.createElement('span');
            // Format time nicely, showing minutes if less than 1 hour
            if (nextSunTime < 1) {
              const minutes = Math.round(nextSunTime * 60);
              predictionValue.textContent = `${minutes} min`;
            } else {
              const hours = Math.floor(nextSunTime);
              const minutes = Math.round((nextSunTime - hours) * 60);
              if (minutes > 0) {
                predictionValue.textContent = `${hours}h ${minutes}m`;
              } else {
                predictionValue.textContent = `${hours}h`;
              }
            }
            predictionValue.style.fontWeight = 'bold';
            predictionValue.style.fontSize = '0.75rem';
            predictionValue.style.color = '#9C27B0'; // Purple for time prediction
            predictionContainer.appendChild(predictionValue);
            
            sunContainer.appendChild(predictionContainer);
          } 
          else if (sunExposure > 0 && sunDuration !== undefined) {
            const durationContainer = document.createElement('div');
            durationContainer.style.display = 'flex';
            durationContainer.style.alignItems = 'center';
            durationContainer.style.justifyContent = 'space-between';
            
            const durationLabel = document.createElement('span');
            durationLabel.textContent = 'In sun for:';
            durationLabel.style.fontWeight = '500';
            durationLabel.style.fontSize = '0.75rem';
            durationContainer.appendChild(durationLabel);
            
            const durationValue = document.createElement('span');
            // Format time nicely, showing minutes if less than 1 hour
            if (sunDuration < 1) {
              const minutes = Math.round(sunDuration * 60);
              durationValue.textContent = `${minutes} min`;
            } else {
              const hours = Math.floor(sunDuration);
              const minutes = Math.round((sunDuration - hours) * 60);
              if (minutes > 0) {
                durationValue.textContent = `${hours}h ${minutes}m`;
              } else {
                durationValue.textContent = `${hours}h`;
              }
            }
            durationValue.style.fontWeight = 'bold';
            durationValue.style.fontSize = '0.75rem';
            durationValue.style.color = '#FF9800'; // Orange for duration
            durationContainer.appendChild(durationValue);
            
            sunContainer.appendChild(durationContainer);
          }
          
          // Add overall rating if available - more compact
          if (pub.rating) {
            const ratingContainer = document.createElement('div');
            ratingContainer.style.display = 'flex';
            ratingContainer.style.alignItems = 'center';
            ratingContainer.style.justifyContent = 'space-between';
            
            const ratingLabel = document.createElement('span');
            ratingLabel.textContent = 'Overall:';
            ratingLabel.style.fontWeight = '500';
            ratingLabel.style.fontSize = '0.75rem';
            ratingContainer.appendChild(ratingLabel);
            
            const ratingValue = document.createElement('span');
            ratingValue.textContent = '🍺'.repeat(Math.round(pub.rating));
            ratingValue.style.color = '#FFD700';
            ratingValue.style.fontSize = '0.85rem';
            ratingContainer.appendChild(ratingValue);
            
            sunContainer.appendChild(ratingContainer);
          }
          
          popupContent.appendChild(sunContainer);
        }
        
        // Add garden status information - more compact
        if (pub.garden_drawn === false) {
          const gardenNotDrawn = document.createElement('p');
          gardenNotDrawn.textContent = 'Garden not mapped yet';
          gardenNotDrawn.style.color = '#FF6347'; // Tomato red
          gardenNotDrawn.style.margin = '4px 0 0 0';
          gardenNotDrawn.style.fontSize = '0.7rem';
          popupContent.appendChild(gardenNotDrawn);
        }
        
        // Add view pub button - more compact
        const buttonContainer = document.createElement('div');
        buttonContainer.style.marginTop = '6px';
        buttonContainer.style.textAlign = 'center';
        
        const viewButton = document.createElement('button');
        viewButton.textContent = 'View Details';
        viewButton.style.backgroundColor = themeColors.blue;
        viewButton.style.color = 'white';
        viewButton.style.border = 'none';
        viewButton.style.padding = '4px 8px';
        viewButton.style.borderRadius = '4px';
        viewButton.style.cursor = 'pointer';
        viewButton.style.fontSize = '0.75rem';
        viewButton.style.fontWeight = 'bold';
        
        // Add hover effect
        viewButton.onmouseover = () => {
          viewButton.style.backgroundColor = themeColors.lightBlue;
        };
        viewButton.onmouseout = () => {
          viewButton.style.backgroundColor = themeColors.blue;
        };
        
        // Add click handler
        viewButton.onclick = (e) => {
          e.stopPropagation();
          // Close popup
          marker.closePopup();
          // Store pub in localStorage for persistence
          localStorage.setItem('selectedPub', JSON.stringify(pub));
          // Navigate to pub page with the new URL format
          navigate(`/pub/${pub.google_place_id}`, { state: { pubToView: pub } });
        };
        
        buttonContainer.appendChild(viewButton);
        popupContent.appendChild(buttonContainer);
        
        // Bind popup to marker with more compact options
        marker.bindPopup(popupContent, {
          maxWidth: 180,
          minWidth: 150,
          closeButton: true,
          autoClose: true,
          className: 'compact-popup'
        });
        
        // Add click handler for marker
        marker.on('click', () => {
          // Open popup
          marker.openPopup();
        });
      });
      
      
      // Fit bounds if we have markers
      if (markers.length > 0 && mapRef.current) {
        try {
          // Wrap in try-catch to prevent errors
          const bounds = L.latLngBounds(markers);
          
          // Use a setTimeout to ensure the map is ready before fitting bounds
          setTimeout(() => {
            if (mapRef.current) {
              try {
                mapRef.current.fitBounds(bounds.pad(0.3));
                
                // Additional invalidate size for good measure
                setTimeout(() => {
                  if (mapRef.current) {
                    mapRef.current.invalidateSize(true);
                  }
                }, 200);
              } catch (e) {
                console.error(`Error fitting bounds: ${e}`);
              }
            }
          }, 100);
        } catch (e) {
          console.error(`Error creating bounds: ${e}`);
        }
      }
    } catch (error) {
      console.error(`Error adding markers: ${error instanceof Error ? error.message : String(error)}`);
    }
  };
  
  // Handle location or pubs changes
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) {
      return;
    }

    try {
      // Delay to ensure map is ready
      setTimeout(() => {
        // Recenter map - safely
        try {
          mapRef.current?.setView([latitude, longitude], mapRef.current.getZoom() || 13);
        } catch (e) {
          console.error(`Error setting view: ${e}`);
        }
        
        // Re-add markers
        addPubMarkers();
        
        // Invalidate size to ensure proper rendering
        setTimeout(() => {
          try {
            mapRef.current?.invalidateSize(true);
          } catch (e) {
            console.error(`Error invalidating size: ${e}`);
          }
        }, 200);
      }, 100);
    } catch (error) {
      console.error(`Error updating map: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [latitude, longitude, pubsToView, mapInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: 'xl',
        margin: '0 auto',
        padding: { xs: '0px 8px 16px', sm: '0px 12px 20px' },
        display: 'flex',
        flexDirection: 'column'
      }}
      key={renderKey}
    >
      <Fonts />
      
      {/* Custom location edit button */}
      {isCustomLocation && (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: themeColors.lightYellow,
            py: 0.5,
            px: 1.5,
            borderRadius: '48px',
            mr: 1
          }}>
            <PlaceIcon sx={{ color: themeColors.orange, mr: 0.5, fontSize: '0.9rem' }} />
            <Typography
              variant="caption"
              sx={{
                color: themeColors.darkBackground,
                fontWeight: 500,
                fontSize: '0.75rem',
                fontFamily: "ApfelGrotezkRegular"
              }}
            >
              Custom Search Area
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditLocationIcon />}
            onClick={handleEditSearchArea}
            sx={{
              background: 'transparent',
              color: 'black',
              fontSize: '0.9rem',
              fontFamily: "ApfelGrotezkRegular",
              borderRadius: '48px',
              transition: 'all 0.2s ease',
              textTransform: 'none',
              border: `2px solid black`,
              py: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderColor: 'black',
              }
            }}
          >
            Change
          </Button>
        </Box>
      )}
      
      {/* Map Container - simplified styling to match other components */}
      <Box sx={{
        height: { xs: '55vh', sm: '60vh', md: '65vh' },
        width: '100%',
        borderRadius: '24px',
        overflow: 'hidden',
        position: 'relative',
        mb: 3,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <div 
          id="map-container"
          ref={mapContainerRef} 
          style={{ 
            width: '100%', 
            height: '100%',
            backgroundColor: '#f0f0f0',
            position: 'relative',
            zIndex: 1,
            display: 'block',
          }}
        />
        
        {!mapInitialized && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <CircularProgress size={60} sx={{ mb: 2, color: themeColors.orange }} />
            <Typography variant="h6" sx={{ fontFamily: "ApfelGrotezkRegular" }}>Initialising Map...</Typography>
          </Box>
        )}
      </Box>
      
      {/* Updated continuous colour scale legend */}
      <Box 
        sx={{
          p: 2, 
          borderRadius: '24px',
          background: themeColors.lightYellow,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}
      >
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 600,
          fontFamily: "ApfelGrotezkRegular",
          color: themeColors.darkBackground,
          marginBottom: 1.5,
          display: 'flex',
          alignItems: 'center'
        }}>
          <WbSunnyIcon sx={{ mr: 0.75, color: themeColors.orange, fontSize: '1.2rem' }} />
          Amount of garden in sun
        </Typography>
        
        {/* Continuous colour scale for sun exposure */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          mb: 2,
          px: 1
        }}>
          {/* Left label - 0% */}
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, minWidth: '30px', fontFamily: "ApfelGrotezkRegular" }}>
            0%
          </Typography>
          
          {/* The actual continuous scale using the hot colormap */}
          <Box sx={{ 
            flex: 1,
            mx: 1.5,
            position: 'relative',
            height: '12px',
            borderRadius: '6px',
            background: `linear-gradient(to right, ${sunExposureColors.join(', ')})`,
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
          }} />
          
          {/* Right label - 100% */}
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, minWidth: '30px', textAlign: 'right', fontFamily: "ApfelGrotezkRegular" }}>
            100%
          </Typography>
        </Box>
        
        {/* Trend indicators with simplified arrows */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            backgroundColor: themeColors.lager,
            borderRadius: '48px',
            padding: '4px 12px'
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#4CAF50',
                fontFamily: "ApfelGrotezkRegular"
              }}
            >
              <ArrowUpwardIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
              Getting sunnier
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            backgroundColor: themeColors.lager,
            borderRadius: '48px',
            padding: '4px 12px'
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#F44336',
                fontFamily: "ApfelGrotezkRegular"
              }}
            >
              <ArrowDownwardIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
              Getting shadier
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MapView;